// Chakra imports
import {
  Button,
  Box,
  Flex,
  Grid,
  HStack,
  Icon,
  Image,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
// Custom components
import { StatisticsPageErrorBanner } from "../../../components/errors/StatisticsPageErrorBanner";
import { StatisticsPageErrorProvider } from "../../../hooks/StatisticsPageErrorContext";
import CardBody from "../../../components/Card/CardBody";
// Drag and drop
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  pointerWithin,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
// assets
import peopleImage from "../../../assets/img/people-image.png";
import logoChakra from "../../../assets/svg/logo-white.svg";
import BarChart from "../../../components/Charts/BarChart";
// Custom icons (removed - no longer needed for MiniStatistics)
import React, { Fragment, useMemo, useState, useEffect, useRef, useCallback, FC } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { dashboardTableData } from "../../../variables/general";
// Services
import { useUser } from "@clerk/clerk-react";
import {
  useTradeStatistics,
  useDashboardSummary,
  useDailyActivity,
  useSymbolStatistics,
  useRiskMetrics,
  useTradeDataFreshMountInvalidation,
} from "../../../hooks/useTradeStatistics";
import { DISPLAY_PREFERENCES_CHANGED_EVENT } from "../../../hooks/useUserNotificationTimezone";
import { useUserNotificationTimezone } from "../../../hooks/useUserNotificationTimezone";
import { useDashboardDateWindowListener } from "../../../hooks/useDashboardDateWindowListener";
import { useDisplayCurrency } from "../../../contexts/DisplayCurrencyContext";
// Types
import { Trade, TradeStatisticsFilters, DateRange } from "../../../types/trade";
import type {
  WinRateTrendResponse,
  PortfolioGrowthResponse,
  DashboardSummaryCardTimeframe,
  DashboardSummaryTimeframesInput,
  DashboardTradingMetrics,
} from "../../../services/tradeStatisticsApi";
import { portfolioGrowthChartTitle } from "../../../utils/portfolioGrowthUi";
import { resolveDailyActivityTimezone } from "../../../utils/tradeAccountAnalytics";
import { isTradeAccountCsvOnlyForUi } from "../../../utils/tradeAccountSource";
import type { DashboardCardType, DashboardLayoutZone } from "../../../types/dashboardLayout";
import DashboardHeaderDateRangePicker from "./components/DashboardHeaderDateRangePicker";
import { DashboardHeaderImportButton } from "./components/DashboardHeaderImportButton";
import { NeedHelpButton } from "@/components/need-help-button";
import { useIsBelowLg, useIsMobile } from "@/hooks/use-mobile";
import {
  dashboardMiniStatsGridTemplateColumns,
  miniStatGridTemplateColumns,
} from "@/utils/miniStatGridLayout";
import {
  buildPreviousPeriodStatisticsFilters,
  endOfIanaCalendarDayUtc,
  percentChangeVsPrevious,
  resolveDashboardPreviousComparisonLocalRange,
  resolveTradeListWindowQuery,
  startOfIanaCalendarDayUtc,
} from "../../../utils/notificationPeriod";
// Utils - statistics now come from backend API only
import Greeting from "./components/Greeting";
import MiniStatisticsV2 from "./components/MiniStatisticsV2";
import PortfolioSizeChart from "./components/PortfolioSizeChart";
import WinRateChart from "./components/WinRateChart";
import TotalTradesChart from "./components/TotalTradesChart";
import { TotalPnLChart } from "../TradeLog/components/TradeLogCharts";
import TradingStreakCard from "./components/TradingStreakCard";
import SortableCard from "./components/SortableCard";
import { DashboardChartRowInsertDropZone } from "./components/DashboardChartRowInsertDropZone";
import TradeActivityCalendar from "./components/TradeActivityCalendar";
import ChartCard from "./components/ChartCard";
import DashboardPortfolioGrowthChart from "./components/DashboardPortfolioGrowthChart";
import { DashboardMilestonesCard } from "./components/DashboardMilestonesCard";
import ErrorBoundary from "../../../components/ErrorBoundary";
import { useActiveBrokerAccount } from "../../../hooks/useActiveBrokerAccount";
import { useActiveBrokerCapabilities } from "../../../hooks/useActiveBrokerCapabilities";
import { useDashboardFetchPlan } from "./hooks/useDashboardFetchPlan";
import DashboardLayoutsMenu from "./components/DashboardLayoutsMenu";
import DashboardEditLayoutToolbar from "./components/DashboardEditLayoutToolbar";
import { DashboardLayoutCardOptionsMenu } from "./components/DashboardLayoutCardOptionsMenu";
import {
  getDashboardLayoutReplaceOptions,
  getDashboardLayoutSelectableCards,
} from "./layout/getDashboardLayoutSelectableCards";
import {
  canAddDashboardMiniStatCard,
  canRemoveDashboardLayoutCard,
  canReplaceDashboardLayoutCard,
  isDashboardLayoutCardEditable,
} from "./layout/dashboardLayoutCardPolicy";
import {
  isMiniStatSlotId,
  miniStatSlotId,
  parseMiniStatSlotId,
  preserveViewportScroll,
} from "./components/dashboardLayoutUi";
import DashboardRenameLayoutDialog from "./components/DashboardRenameLayoutDialog";
import DashboardDeleteLayoutDialog from "./components/DashboardDeleteLayoutDialog";
import DashboardAddWidgetModal from "./components/DashboardAddWidgetModal";
import { DashboardLayoutProvider, useDashboardLayoutStore } from "./layout/dashboardLayoutStore";
import type { DashboardDefaultLayoutVariant } from "./layout/dashboardCardRegistry";
import {
  createBackendDashboardLayoutAdapter,
} from "./layout/dashboardLayoutPersistence";
import {
  dashboardLayoutAccountScopeKey,
  isDashboardLayoutAccountScopeReady,
  type DashboardLayoutAccountScope,
} from "../../../types/dashboardLayoutAccountScope";
import {
  DASHBOARD_MAX_MINI_STAT_SLOTS,
  dashboardCardRegistry,
} from "./layout/dashboardCardRegistry";
import PnLOverTimeChart from "../Statistics/components/PnLOverTimeChart";
import TradeDistributionCharts from "../Statistics/components/TradeDistributionCharts";
import PerformanceBreakdown from "../Statistics/components/PerformanceBreakdown";
import StreakAnalysis from "../Statistics/components/StreakAnalysis";
import ComparativeAnalysis from "../Statistics/components/ComparativeAnalysis";
import RiskMetricsTab from "../Statistics/components/RiskMetricsTab";
import {
  StatisticsEmptyState,
  statisticsPageMinHeight,
} from "../Statistics/components/statisticsEmptyState";
import { useShadcnInsetChrome } from "@/contexts/ShadcnInsetChromeContext";
import { useShadcnInsetHeaderActions } from "@/contexts/ShadcnInsetHeaderActionsContext";
import {
  dateRangeForTradeLogFromPersisted,
  deriveTimeframeKeyFromLocalDateRange,
  dashboardPresetChartPeriodLabel,
  equalDateRange,
  getActiveDashboardDatePresetId,
  getInitialDashboardDateWindow,
  isDashboardDatePresetTimeframe,
  persistStateFromTradeLog,
  preferredPresetFromPersisted,
  writeDashboardDateWindow,
  type DashboardDatePresetTimeframe,
} from "../../../utils/dashboardDateWindowStorage";
import {
  applyDashboardLayoutMove,
  getDashboardLayoutMoveState,
  moveMiniStatSlot,
  swapSplitRowCards,
} from "./layout/dashboardLayoutMove";
import {
  addSplittableChartRowCard,
  applyChartRowDragEnd,
  applyChartRowInsertDrag,
  buildDashboardChartRows,
  chartRowInsertDropId,
  getChartRowMergePreview,
  isSplittableChartRowCard,
  parseChartRowInsertDropId,
  type ChartRowMergePreview,
  type DashboardChartRow,
} from "./layout/dashboardChartRows";

const SPLIT_ROW_CARD_TYPES: DashboardCardType[] = [
  "portfolioGrowth",
  "winRateTrend",
  "pnlMilestones",
];

const CHART_ROW_FLEX_PROPS = {
  display: { base: "block", lg: "flex" } as const,
  alignItems: "stretch" as const,
  gap: { base: "24px", lg: "24px" } as const,
  w: "100%",
};

const CHART_ROW_COLUMN_PROPS = {
  "data-dashboard-chart-row-column": "",
  flex: { base: "none", lg: 1 } as const,
  minW: "0",
  w: "100%",
  display: "flex",
  flexDirection: "column" as const,
  alignSelf: "stretch" as const,
};

/** Prefer the card under the pointer so large chart rows don't lose hover during pairing drags. */
const dashboardLayoutCollisionDetection: CollisionDetection = (args) => {
  const pointerHits = pointerWithin(args);
  if (pointerHits.length > 0) return pointerHits;
  return closestCenter(args);
};

function DashboardContent() {
  const shadcnInset = useShadcnInsetChrome();
  const isMobile = useIsMobile();
  const isBelowLg = useIsBelowLg();
  useTradeDataFreshMountInvalidation();
  const { mountEl: insetHeaderMountEl, setActions: setInsetHeaderActions } =
    useShadcnInsetHeaderActions();
  const { user } = useUser();
  const history = useHistory();
  const { activeAccountCode, activeBrokerName, accounts: tradeAccounts, loading: brokerAccountsLoading } =
    useActiveBrokerAccount();
  const { timezone: notificationTimezone } = useUserNotificationTimezone();
  const { formatMoney } = useDisplayCurrency();
  const reportingTimeZone = notificationTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const { resolvedTheme } = useTheme();
  const [allTrades, setAllTrades] = useState<Trade[]>([]);
  const [isLoadingTrades, setIsLoadingTrades] = useState(false);
  
  // Same date window as Trade Log / Statistics / Reports (localStorage + broadcast)
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    dateRangeForTradeLogFromPersisted(getInitialDashboardDateWindow())
  );
  const [selectedPresetId, setSelectedPresetId] =
    useState<DashboardDatePresetTimeframe | null>(() =>
      preferredPresetFromPersisted(getInitialDashboardDateWindow())
    );
  const {
    activeLayoutId,
    isEditMode,
    getLayoutItems,
    getVisibleItems,
    getActiveLayout,
    setActiveLayout,
    createLayout,
    abandonNewLayout,
    renameLayout,
    deleteLayout,
    canDeleteLayout,
    updateLayoutItems,
    addCard,
    removeCard,
    saveEditModeChanges,
    cancelEditModeChanges,
    setEditMode,
    isHydrating,
    isMutating,
    pendingMutation,
    persistenceError,
  } = useDashboardLayoutStore();
  const {
    isOpen: isAddWidgetModalOpen,
    onOpen: onAddWidgetModalOpen,
    onClose: onAddWidgetModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteLayoutModalOpen,
    onOpen: onDeleteLayoutModalOpen,
    onClose: onDeleteLayoutModalClose,
  } = useDisclosure();
  const {
    isOpen: isRenameLayoutModalOpen,
    onOpen: onRenameLayoutModalOpen,
    onClose: onRenameLayoutModalClose,
  } = useDisclosure();
  const [addWidgetZone, setAddWidgetZone] = useState<"miniStats" | "splitRow" | "fullWidth" | null>(null);
  const [replaceCardSlot, setReplaceCardSlot] = useState<DashboardCardType | null>(null);
  const [fullWidthAddAnchor, setFullWidthAddAnchor] = useState<"belowTradeDistribution" | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isNewLayoutCreateSession, setIsNewLayoutCreateSession] = useState(false);
  const [renameModalIntent, setRenameModalIntent] = useState<"toolbar" | "createBeforeEdit">("toolbar");
  const newLayoutPreviousLayoutIdRef = useRef<string | null>(null);

  const derivedChartTimeframe = useMemo(
    () => deriveTimeframeKeyFromLocalDateRange(dateRange),
    [dateRange]
  );

  const useHourlyCharts = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) return false;
    return (
      dateRange.startDate.getFullYear() === dateRange.endDate.getFullYear() &&
      dateRange.startDate.getMonth() === dateRange.endDate.getMonth() &&
      dateRange.startDate.getDate() === dateRange.endDate.getDate()
    );
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    writeDashboardDateWindow(
      persistStateFromTradeLog(dateRange, derivedChartTimeframe, selectedPresetId)
    );
  }, [dateRange, derivedChartTimeframe, selectedPresetId]);

  useDashboardDateWindowListener((p) => {
    const next = dateRangeForTradeLogFromPersisted(p);
    setDateRange((prev) => (equalDateRange(prev, next) ? prev : next));
    setSelectedPresetId(preferredPresetFromPersisted(p));
  });

  // Drag and drop sensors with activation delay to prevent accidental drags
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [chartRowDragActiveId, setChartRowDragActiveId] = useState<string | null>(null);
  const [chartRowDragOverId, setChartRowDragOverId] = useState<string | null>(null);
  const chartRowMergePreviewRef = useRef<ChartRowMergePreview | null>(null);

  const clearLayoutDragState = useCallback(() => {
    setChartRowDragActiveId(null);
    setChartRowDragOverId(null);
    chartRowMergePreviewRef.current = null;
  }, []);

  const zoneOfCard = useCallback(
    (layout: { items: { cardType: DashboardCardType; zone: string }[] }, cardType: DashboardCardType) =>
      layout.items.find((i) => i.cardType === cardType)?.zone,
    []
  );

  // Handle drag end (mini stats, split row, full-width tail)
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeLayout = getActiveLayout();
    if (!activeLayout) return;

    if (isMiniStatSlotId(active.id) && isMiniStatSlotId(over.id)) {
      const oldIndex = parseMiniStatSlotId(active.id);
      const newIndex = parseMiniStatSlotId(over.id);
      if (oldIndex === null || newIndex === null || oldIndex === newIndex) return;

      const miniItems = activeLayout.items
        .filter((item) => item.zone === "miniStats" && item.visible)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const orderedIds = miniItems.map((item) => item.cardType);
      const nextOrder = arrayMove(orderedIds, oldIndex, newIndex);
      const updatedItems = activeLayout.items.map((item) => {
        if (item.zone !== "miniStats") return item;
        const idx = nextOrder.indexOf(item.cardType);
        return { ...item, order: idx === -1 ? item.order : idx };
      });
      updateLayoutItems(activeLayout.id, updatedItems);
      return;
    }

    const activeId = active.id as DashboardCardType;
    const overId = over.id as DashboardCardType | string;

    const insertIndex = parseChartRowInsertDropId(overId);
    if (insertIndex !== null && isSplittableChartRowCard(activeId)) {
      const updatedItems = applyChartRowInsertDrag(activeLayout.items, activeId, insertIndex);
      updateLayoutItems(activeLayout.id, updatedItems);
      return;
    }

    const overCardId = overId as DashboardCardType;
    const zA = zoneOfCard(activeLayout, activeId);
    const zB = zoneOfCard(activeLayout, overCardId);
    if (!zA || !zB || zA !== zB) return;

    if (zA === "splitRow" && SPLIT_ROW_CARD_TYPES.includes(activeId) && SPLIT_ROW_CARD_TYPES.includes(overCardId)) {
      const updatedItems = swapSplitRowCards(activeLayout.items, activeId, overCardId);
      updateLayoutItems(activeLayout.id, updatedItems);
      return;
    }

    if (
      zA === "fullWidth" &&
      zB === "fullWidth" &&
      isSplittableChartRowCard(activeId) &&
      isSplittableChartRowCard(overCardId)
    ) {
      const updatedItems = applyChartRowDragEnd(activeLayout.items, activeId, overCardId);
      updateLayoutItems(activeLayout.id, updatedItems);
    }
  };
  
  // Starting balance - should come from user settings, not hardcoded
  // For now, set to 0 if no trades exist
  const startingBalance = useMemo(() => {
    // If no trades, starting balance is 0
    if (allTrades.length === 0) {
      return 0;
    }
    // TODO: Get starting balance from user profile/settings
    // For now, calculate from trades if available
    return 0; // No mock data - should come from backend user settings
  }, [allTrades]);

  const effectiveBrokerAccountCode = activeAccountCode || "";
  const analyticsReady = Boolean(effectiveBrokerAccountCode) && !brokerAccountsLoading;

  const layoutItemsForFetch = useMemo(
    () => getLayoutItems(activeLayoutId),
    [activeLayoutId, getLayoutItems]
  );
  const visibleItemsForFetch = useMemo(
    () => getVisibleItems(activeLayoutId),
    [activeLayoutId, getVisibleItems]
  );
  const visibleCardTypeSet = useMemo(
    () => new Set(visibleItemsForFetch.map((item) => item.cardType)),
    [visibleItemsForFetch]
  );
  const fetchPlan = useDashboardFetchPlan(visibleCardTypeSet, {
    ready: analyticsReady && !isHydrating,
  });
  const needsRiskMetrics =
    visibleCardTypeSet.has("keyRiskMetrics") ||
    visibleCardTypeSet.has("drawdownOverTime") ||
    visibleCardTypeSet.has("equityPeakPath");

  const previousComparisonLocalRange = useMemo(
    () =>
      resolveDashboardPreviousComparisonLocalRange(
        dateRange || { startDate: null, endDate: null },
        derivedChartTimeframe,
        reportingTimeZone,
        { includeTimezoneQuery: true }
      ),
    [dateRange, derivedChartTimeframe, reportingTimeZone]
  );

  const statisticsFilters = useMemo<TradeStatisticsFilters | undefined>(() => {
    if (!effectiveBrokerAccountCode) return undefined;
    const window = resolveTradeListWindowQuery(
      dateRange || { startDate: null, endDate: null },
      derivedChartTimeframe,
      reportingTimeZone,
      { includeTimezoneQuery: true }
    );

    const apiFilters: TradeStatisticsFilters = {
      broker_account_code: effectiveBrokerAccountCode,
      ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
      timezone: reportingTimeZone,
    };

    if (window.kind === "local") {
      apiFilters.local_start_date = window.period.local_start_date;
      apiFilters.local_end_date = window.period.local_end_date;
      if (window.period.timezone) {
        apiFilters.timezone = window.period.timezone;
      }
    } else {
      apiFilters.timeframe = window.timeframe;
    }

    if (previousComparisonLocalRange) {
      apiFilters.previous_start_date = startOfIanaCalendarDayUtc(
        previousComparisonLocalRange.local_start_date,
        reportingTimeZone
      );
      apiFilters.previous_end_date = endOfIanaCalendarDayUtc(
        previousComparisonLocalRange.local_end_date,
        reportingTimeZone
      );
    }

    return apiFilters;
  }, [
    dateRange,
    derivedChartTimeframe,
    effectiveBrokerAccountCode,
    activeBrokerName,
    reportingTimeZone,
    previousComparisonLocalRange,
  ]);
  const { statistics: dashboardStats, loading: dashboardStatsLoading } = useTradeStatistics(statisticsFilters, {
    enabled: fetchPlan.ready && fetchPlan.needsStatistics,
  });

  const previousPeriodStatisticsFilters = useMemo(
    () =>
      statisticsFilters
        ? buildPreviousPeriodStatisticsFilters(
            statisticsFilters,
            dateRange || { startDate: null, endDate: null },
            derivedChartTimeframe,
            reportingTimeZone
          )
        : undefined,
    [statisticsFilters, dateRange, derivedChartTimeframe, reportingTimeZone]
  );
  const { statistics: previousPeriodDashboardStats } = useTradeStatistics(
    previousPeriodStatisticsFilters,
    { enabled: fetchPlan.ready && fetchPlan.needsPreviousPeriodStatistics }
  );
  const { symbolStats, loading: symbolStatsLoading } = useSymbolStatistics(statisticsFilters, {
    enabled: fetchPlan.ready && fetchPlan.needsSymbolStatistics,
  });
  const {
    data: sharedRiskMetricsData,
    loading: sharedRiskMetricsLoading,
    error: sharedRiskMetricsError,
    errorAction: sharedRiskMetricsErrorAction,
  } = useRiskMetrics(statisticsFilters, {
    enabled: fetchPlan.ready && needsRiskMetrics,
  });
  const {
    supportsPortfolioGrowth,
    supportsPortfolioBalanceFromBroker,
    accountAnalytics,
    defaultGrowthBasis,
  } = useActiveBrokerCapabilities(effectiveBrokerAccountCode, activeBrokerName, {
    skipCapabilitiesFetch: true,
  });

  const dailyActivityTimeZone = useMemo(
    () => resolveDailyActivityTimezone(accountAnalytics, reportingTimeZone),
    [accountAnalytics, reportingTimeZone]
  );

  // `/dashboard/summary` accepts the same rolling presets as statistics *or* `start_date` + `end_date` for a fixed window.
  // `deriveTimeframeKeyFromLocalDateRange` can yield `last-week` (not a summary param) or `all-time` when the picker
  // range does not match an anchored preset — in those cases we must send explicit dates so charts match Trade Log.
  const dashboardSummaryTimeframes = useMemo((): DashboardSummaryTimeframesInput => {
    const roi = "all-time" as const;
    const window = resolveTradeListWindowQuery(
      dateRange || { startDate: null, endDate: null },
      derivedChartTimeframe,
      reportingTimeZone,
      { includeTimezoneQuery: true }
    );

    if (window.kind === "local") {
      const zone = window.period.timezone || reportingTimeZone || "UTC";
      const start = startOfIanaCalendarDayUtc(window.period.local_start_date, zone);
      const end = endOfIanaCalendarDayUtc(window.period.local_end_date, zone);
      return {
        start_date: start.toISOString(),
        end_date: end.toISOString(),
        local_start_date: window.period.local_start_date,
        local_end_date: window.period.local_end_date,
        timezone: zone,
        roi,
      };
    }

    const tf: DashboardSummaryCardTimeframe = window.timeframe;
    return {
      portfolio: tf,
      winRate: tf,
      pnl: tf,
      portfolioGrowth: tf,
      winRateTrend: tf,
      roi,
    };
  }, [dateRange, derivedChartTimeframe, reportingTimeZone]);

  /** Civil dates for clipping growth / win-rate series to the picker (avoids prior-month anchor buckets). */
  const chartLocalWindow = useMemo(() => {
    const window = resolveTradeListWindowQuery(
      dateRange || { startDate: null, endDate: null },
      derivedChartTimeframe,
      reportingTimeZone,
      { includeTimezoneQuery: true }
    );
    if (window.kind !== "local") return null;
    return {
      localStart: window.period.local_start_date,
      localEnd: window.period.local_end_date,
    };
  }, [dateRange, derivedChartTimeframe, reportingTimeZone]);

  // Dashboard summary and daily activity are requested in parallel (both hooks run when brokerAccountCode is set).
  const { data: dashboardSummary, loading: dashboardSummaryLoading, error: dashboardSummaryError, errorAction: dashboardSummaryErrorAction, refetch: refetchDashboardSummary } =
    useDashboardSummary(effectiveBrokerAccountCode, dashboardSummaryTimeframes, activeBrokerName, {
      // Intent from visible cards — do not require fetchPlan.ready (hydrate used to wipe summary).
      enabled: analyticsReady && fetchPlan.needsDashboardSummary,
    });

  const dashboardPageError = useMemo(() => {
    const failures: Array<{ message: string; action?: string | null }> = [];
    if (dashboardSummaryError?.trim()) {
      failures.push({ message: dashboardSummaryError, action: dashboardSummaryErrorAction });
    }
    if (sharedRiskMetricsError?.trim()) {
      failures.push({ message: sharedRiskMetricsError, action: sharedRiskMetricsErrorAction });
    }
    if (failures.length < 2) return null;
    const first = failures[0];
    return {
      message: first.message || "Couldn't load dashboard. Refresh or try again.",
      action: first.action ?? undefined,
    };
  }, [
    dashboardSummaryError,
    dashboardSummaryErrorAction,
    sharedRiskMetricsError,
    sharedRiskMetricsErrorAction,
  ]);

  useEffect(() => {
    const onTzPrefs = (ev: Event) => {
      const d = (ev as CustomEvent<{ setting?: string }>).detail;
      if (d?.setting === "timezone") {
        refetchDashboardSummary();
      }
    };
    window.addEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onTzPrefs as EventListener);
    return () =>
      window.removeEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onTzPrefs as EventListener);
  }, [refetchDashboardSummary]);

  // Daily activity for the trade calendar should stay all-time so month overview is always available.
  const dailyActivityFilters = useMemo(() => {
    if (!effectiveBrokerAccountCode) return undefined;
    return {
      broker_account_code: effectiveBrokerAccountCode,
      ...(activeBrokerName ? { broker_name: activeBrokerName } : {}),
      timezone: dailyActivityTimeZone,
    } as TradeStatisticsFilters;
  }, [effectiveBrokerAccountCode, activeBrokerName, dailyActivityTimeZone]);
  const { data: dailyActivityData } = useDailyActivity(dailyActivityFilters, {
    enabled: fetchPlan.ready && fetchPlan.needsDailyActivity,
  });
  
  // Trades come from dashboard summary only (no separate trades endpoint call)
  useEffect(() => {
    if (!user?.id) {
      setAllTrades([]);
      return;
    }

    if (dashboardSummary?.recentTrades) {
      try {
        const frontendTrades: Trade[] = dashboardSummary.recentTrades.map((trade) => {
          const raw = trade as { entry_quantity?: number; exit_date?: string };
          const entryQty = raw.entry_quantity ?? (trade as { entryQuantity?: number }).entryQuantity;
          const qty = entryQty != null && !Number.isNaN(Number(entryQty)) ? Number(entryQty) : 0;
          const exitDate = trade.exitDate ?? raw.exit_date;
          return {
            id: trade.id,
            symbol: trade.symbol,
            type: trade.type,
            status: trade.status,
            entryDate: trade.entryDate,
            exitDate: exitDate ?? undefined,
            entryPrice: trade.entryPrice,
            exitPrice: trade.exitPrice,
            netPnL: trade.netPnL,
            returnPercent: trade.returnPercent,
            entryQuantity: qty,
            entryFees: 0,
            entryValue: 0,
            createdAt: trade.entryDate,
            updatedAt: trade.entryDate,
            userId: user.id,
          };
        });
        setAllTrades(frontendTrades);
      } catch (error) {
        console.error("Error converting dashboard summary trades:", error);
        setAllTrades([]);
      }
      setIsLoadingTrades(false);
    } else if (!dashboardSummaryLoading) {
      setAllTrades([]);
      setIsLoadingTrades(false);
    }
  }, [user?.id, dashboardSummary?.recentTrades, dashboardSummaryLoading]);

  // Signal when page content is fully loaded (for loading screen)
  // Note: We don't dispatch this event here anymore - let ProtectedRoute detect
  // when all loading indicators (spinners, skeletons) are gone instead.
  // This ensures charts have time to fully render.

  // Extract portfolio stats from dashboard summary
  const portfolioStats = dashboardSummary ? {
    portfolio_value: dashboardSummary.portfolio.portfolio_value,
    portfolio_change_percent: dashboardSummary.portfolio.portfolio_change_percent,
  } : null;
  const portfolioStatsLoading = dashboardSummaryLoading;
  const refetchPortfolioStats = refetchDashboardSummary;

  // Portfolio statistics - simplified (dashboard summary doesn't include detailed stats)
  const portfolioStatistics = useMemo(() => {
    // Return empty stats - dashboard summary only provides portfolio_value and portfolio_change_percent
    return {
      totalTrades: 0,
      openTrades: 0,
      closedTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      winRate: 0,
      totalPnL: 0,
      averageWin: 0,
      averageLoss: 0,
      largestWin: 0,
      largestLoss: 0,
      totalReturnPercent: 0,
      averageReturnPercent: 0,
    };
  }, []);

  // Portfolio size from dashboard summary
  const portfolioSize = useMemo(() => {
    if (dashboardSummary?.portfolio.portfolio_value !== null && dashboardSummary?.portfolio.portfolio_value !== undefined) {
      return dashboardSummary.portfolio.portfolio_value;
    }
    // Fallback calculation if backend doesn't provide portfolio_value
    return startingBalance;
  }, [dashboardSummary?.portfolio.portfolio_value, startingBalance]);

  // Portfolio change percentage from dashboard summary
  const portfolioChangePercent = useMemo(() => {
    if (dashboardSummary?.portfolio.portfolio_change_percent !== null && dashboardSummary?.portfolio.portfolio_change_percent !== undefined) {
      return dashboardSummary.portfolio.portfolio_change_percent;
    }
    return null;
  }, [dashboardSummary?.portfolio.portfolio_change_percent]);

  // Portfolio growth data from dashboard summary (support both camelCase and snake_case from API)
  const portfolioGrowthData = useMemo((): PortfolioGrowthResponse | null | undefined => {
    const d = dashboardSummary as { portfolioGrowth?: PortfolioGrowthResponse; portfolio_growth?: PortfolioGrowthResponse } | null | undefined;
    return d?.portfolioGrowth ?? d?.portfolio_growth;
  }, [dashboardSummary]);

  const summaryHasPortfolioGrowthSeries = Boolean(portfolioGrowthData?.data_points?.length);
  // Portfolio growth change percentage from dashboard summary
  const portfolioGrowthChangePercent = useMemo(() => {
    const changePercent = portfolioGrowthData?.total_change_percent;
    if (changePercent !== null && changePercent !== undefined && !Number.isNaN(changePercent)) {
      return Number(changePercent);
    }
    const pnlChange = dashboardSummary?.pnl?.pnl_change_percent;
    if (pnlChange !== null && pnlChange !== undefined && !Number.isNaN(pnlChange)) {
      return Number(pnlChange);
    }
    if (startingBalance === 0) return 0;
    return ((portfolioSize - startingBalance) / startingBalance) * 100;
  }, [
    portfolioGrowthData?.total_change_percent,
    dashboardSummary?.pnl?.pnl_change_percent,
    portfolioSize,
    startingBalance,
  ]);

  /** Chart title from summary `growth_basis` + capabilities (PnL-only curves use Total P&L Trend). */
  const dashboardPortfolioGrowthTitle = useMemo(
    () =>
      portfolioGrowthChartTitle({
        growthBasis: portfolioGrowthData?.growth_basis,
        supportsPortfolioGrowth,
        defaultGrowthBasis,
      }),
    [portfolioGrowthData?.growth_basis, supportsPortfolioGrowth, defaultGrowthBasis]
  );

  // Total P&L — prefer dashboard summary, fallback to statistics
  const monthlyPnL = useMemo(() => {
    const fromSummary = dashboardSummary?.pnl?.total_pnl;
    if (fromSummary != null && !Number.isNaN(Number(fromSummary))) {
      return Number(fromSummary);
    }
    if (!dashboardStats) {
      return 0;
    }
    return dashboardStats.total_pnl ?? 0;
  }, [dashboardSummary?.pnl?.total_pnl, dashboardStats]);
  const expectancyValue = useMemo(() => {
    const fromSummary = dashboardSummary?.tradingMetrics?.expectancy;
    if (fromSummary != null && !Number.isNaN(Number(fromSummary))) {
      return Number(fromSummary);
    }
    if (!dashboardStats && !dashboardSummary) return 0;
    return (
      (dashboardStats as any)?.expectancy ??
      (dashboardStats as any)?.average_per_trade_pnl ??
      (dashboardSummary as any)?.expectancy ??
      0
    );
  }, [dashboardStats, dashboardSummary]);
  
  const dashboardDateRangeShortLabel = useMemo(() => {
    if (!dateRange.startDate && !dateRange.endDate) return "since all time";
    if (dateRange.startDate && dateRange.endDate) {
      const s = dateRange.startDate;
      const e = dateRange.endDate;
      if (
        s.getFullYear() === e.getFullYear() &&
        s.getMonth() === e.getMonth() &&
        s.getDate() === e.getDate()
      ) {
        return "today";
      }
      return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return "selected range";
  }, [dateRange.startDate, dateRange.endDate]);

  const miniStatsComparisonText = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) return undefined;
    const activePreset = getActiveDashboardDatePresetId(dateRange, selectedPresetId);
    if (!activePreset) return "vs prior period";
    return undefined;
  }, [dateRange.startDate, dateRange.endDate, selectedPresetId]);

  // Win rate — prefer dashboard summary, fallback to statistics
  const winRateStats = useMemo(() => {
    const fromSummary = dashboardSummary?.winRate?.win_rate;
    const winRate =
      fromSummary != null && !Number.isNaN(Number(fromSummary))
        ? Number(fromSummary)
        : dashboardStats?.win_rate != null && !Number.isNaN(dashboardStats.win_rate)
          ? Number(dashboardStats.win_rate)
          : 0;
    return {
      totalTrades: 0,
      openTrades: 0,
      closedTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      winRate,
      totalPnL: 0,
      averageWin: 0,
      averageLoss: 0,
      largestWin: 0,
      largestLoss: 0,
      totalReturnPercent: 0,
      averageReturnPercent: 0,
    };
  }, [dashboardSummary?.winRate?.win_rate, dashboardStats?.win_rate]);

  // Win rate change percent — prefer summary, fallback to statistics
  const winRateChangePercent = useMemo(() => {
    const fromSummary = dashboardSummary?.winRate?.win_rate_change_percent;
    if (fromSummary != null && fromSummary !== undefined && !Number.isNaN(Number(fromSummary))) {
      return Number(fromSummary);
    }
    if (
      dashboardStats?.win_rate_change_percent !== null &&
      dashboardStats?.win_rate_change_percent !== undefined
    ) {
      return dashboardStats.win_rate_change_percent;
    }
    return percentChangeVsPrevious(winRateStats.winRate, previousPeriodDashboardStats?.win_rate);
  }, [
    dashboardSummary?.winRate?.win_rate_change_percent,
    dashboardStats?.win_rate_change_percent,
    winRateStats.winRate,
    previousPeriodDashboardStats?.win_rate,
  ]);

  // Total P&L change percent — prefer summary, then period-over-period statistics
  const monthlyPnLChangePercent = useMemo(() => {
    const fromSummary = dashboardSummary?.pnl?.pnl_change_percent;
    if (fromSummary != null && fromSummary !== undefined && !Number.isNaN(Number(fromSummary))) {
      return Number(fromSummary);
    }
    if (
      dashboardStats?.pnl_vs_previous_percent !== null &&
      dashboardStats?.pnl_vs_previous_percent !== undefined
    ) {
      return dashboardStats.pnl_vs_previous_percent;
    }
    if (
      dashboardStats?.pnl_change_percent !== null &&
      dashboardStats?.pnl_change_percent !== undefined
    ) {
      return dashboardStats.pnl_change_percent;
    }
    return percentChangeVsPrevious(monthlyPnL, previousPeriodDashboardStats?.total_pnl);
  }, [
    dashboardSummary?.pnl?.pnl_change_percent,
    dashboardStats?.pnl_vs_previous_percent,
    dashboardStats?.pnl_change_percent,
    monthlyPnL,
    previousPeriodDashboardStats?.total_pnl,
  ]);

  /** Period-over-period % for optional mini stats (no dedicated backend change fields). */
  const miniStatPeriodChanges = useMemo(() => {
    const cur = dashboardStats;
    const prev = previousPeriodDashboardStats;
    const vsPrev = (current: number | null | undefined, previous: number | null | undefined) =>
      percentChangeVsPrevious(current, previous) ?? 0;
    return {
      averageWin: vsPrev(cur?.average_win, prev?.average_win),
      averageLoss: vsPrev(cur?.average_loss, prev?.average_loss),
      largestWin: vsPrev(cur?.largest_win, prev?.largest_win),
      largestLoss: vsPrev(cur?.largest_loss, prev?.largest_loss),
      activeTrades: vsPrev(cur?.open_trades, prev?.open_trades),
      closedTrades:
        cur?.closed_trades_vs_previous_percent != null &&
        cur.closed_trades_vs_previous_percent !== undefined
          ? cur.closed_trades_vs_previous_percent
          : cur?.closed_trades_change_percent != null &&
              cur.closed_trades_change_percent !== undefined
            ? cur.closed_trades_change_percent
            : vsPrev(cur?.closed_trades, prev?.closed_trades),
    };
  }, [dashboardStats, previousPeriodDashboardStats]);

  // Win Rate Trend data from dashboard summary (support both camelCase and snake_case from API)
  const winRateTrendData = useMemo((): WinRateTrendResponse | null | undefined => {
    const d = dashboardSummary as { winRateTrend?: WinRateTrendResponse; win_rate_trend?: WinRateTrendResponse } | null | undefined;
    return d?.winRateTrend ?? d?.win_rate_trend;
  }, [dashboardSummary]);

  const summaryHasWinRateTrendSeries = Boolean(winRateTrendData?.data_points?.length);
  const closedTradesCount = useMemo(() => {
    const fromSummary =
      dashboardSummary?.pnl?.closed_trades ??
      dashboardSummary?.tradingMetrics?.closed_trades_count;
    if (fromSummary != null && fromSummary !== undefined) return fromSummary;
    return dashboardStats?.closed_trades ?? 0;
  }, [
    dashboardSummary?.pnl?.closed_trades,
    dashboardSummary?.tradingMetrics?.closed_trades_count,
    dashboardStats?.closed_trades,
  ]);

  const activeTradesCount = useMemo(() => {
    const fromSummary = dashboardSummary?.pnl?.open_trades;
    if (fromSummary != null && fromSummary !== undefined) return fromSummary;
    return dashboardStats?.open_trades ?? 0;
  }, [dashboardSummary?.pnl?.open_trades, dashboardStats?.open_trades]);

  // Win Rate Trend change percentage from dashboard summary
  const winRateTrendChangePercent = useMemo(() => {
    const changePercent = winRateTrendData?.win_rate_change_percent;
    if (changePercent !== null && changePercent !== undefined && !Number.isNaN(changePercent)) {
      return Number(changePercent);
    }
    return 0;
  }, [winRateTrendData?.win_rate_change_percent]);

  // Latest cumulative win rate from Win Rate Trend chart (for ChartCard display when not using summary)
  const [latestCumulativeWinRate, setLatestCumulativeWinRate] = useState<number>(0);
  // Dashboard Win Rate card: local toggle for average win rate line
  const [showAverageWinRateDashboard, setShowAverageWinRateDashboard] = useState<boolean>(true);

  // Win rate value for Win Rate Trend card: prefer summary (winRate.win_rate or winRateTrend.current_win_rate), else from chart
  const winRateTrendDisplayValue = useMemo(() => {
    const fromWinRate = dashboardStats?.win_rate;
    if (fromWinRate != null && !Number.isNaN(fromWinRate)) return Number(fromWinRate);
    const fromTrend = winRateTrendData?.current_win_rate;
    if (fromTrend != null && !Number.isNaN(fromTrend)) return Number(fromTrend);
    return latestCumulativeWinRate || 0;
  }, [dashboardStats?.win_rate, winRateTrendData?.current_win_rate, latestCumulativeWinRate]);

  // ROI data from dashboard summary
  const roiData = dashboardSummary?.roi;
  const roiPercent = roiData?.roi_percent ?? null;
  const totalInvested = roiData?.total_invested ?? 0;

  /** P&L-window metrics from `/dashboard/summary` (expectancy, max drawdown, scope, …). */
  const tradingMetricsData = useMemo((): DashboardTradingMetrics | null => {
    const d = dashboardSummary as {
      tradingMetrics?: DashboardTradingMetrics;
      trading_metrics?: DashboardTradingMetrics;
    } | null | undefined;
    return d?.tradingMetrics ?? d?.trading_metrics ?? null;
  }, [dashboardSummary]);

  /** Prefer summary tradingMetrics.expectancy (aligned with P&L timeframe) when present. */
  const displayExpectancyValue = useMemo(() => {
    const ex = tradingMetricsData?.expectancy;
    if (ex != null && !Number.isNaN(Number(ex))) {
      return Number(ex);
    }
    return expectancyValue;
  }, [tradingMetricsData, expectancyValue]);

  const tradingMetricsScopeLabel = useMemo(() => {
    const tm = tradingMetricsData;
    if (!tm) return dashboardDateRangeShortLabel;
    if (tm.scope_start && tm.scope_end) {
      const s = new Date(tm.scope_start);
      const e = new Date(tm.scope_end);
      if (!Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) {
        return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
      }
      return "Custom range";
    }
    const st = tm.scope_timeframe;
    if (typeof st === "string" && isDashboardDatePresetTimeframe(st)) {
      return dashboardPresetChartPeriodLabel(st);
    }
    return dashboardDateRangeShortLabel;
  }, [tradingMetricsData, dashboardDateRangeShortLabel]);

  /** When portfolio growth is unavailable (e.g. cash transfers not mapped), show expectancy + max drawdown instead of balance + ROI. */
  const useTradingMetricsStrip = !supportsPortfolioGrowth;

  // Add CSS to ensure grid lines are visible
  useEffect(() => {
    const styleId = "dashboard-chart-grid-styles";
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .apexcharts-svg .apexcharts-gridline:not([class*="heatmap"]),
      .apexcharts-svg .apexcharts-gridlines-horizontal:not([class*="heatmap"]) line,
      .apexcharts-svg .apexcharts-gridlines-vertical:not([class*="heatmap"]) line {
        stroke-dasharray: 5 5 !important;
        stroke: #e7e7e7 !important;
        opacity: 1 !important;
        display: block !important;
        visibility: visible !important;
      }
      .apexcharts-svg .apexcharts-gridlines-horizontal:not([class*="heatmap"]),
      .apexcharts-svg .apexcharts-gridlines-vertical:not([class*="heatmap"]) {
        display: block !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  // Add tooltip styling for Win Rate chart (matching Portfolio Growth)
  useEffect(() => {
    const styleId = "win-rate-tooltip-styles";
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    // Light mode: white background, 4px border, 15px radius
    // Dark mode: gray.700 background, 1px border, 12px radius
    const isLightMode = resolvedTheme !== "dark";
    const tooltipBg = isLightMode ? "#ffffff" : "#2D3748";
    const tooltipBorder = isLightMode ? "#E5E7EB" : "rgba(255, 255, 255, 0.2)";
    const tooltipBorderWidth = isLightMode ? "4px" : "1px";
    const tooltipBorderRadius = isLightMode ? "15px" : "12px";
    const tooltipTextColor = isLightMode ? "#A0AEC0" : "#ffffff";
    const tooltipLabelColor = isLightMode ? "#A0AEC0" : "#c8cfca";
    const borderColor = tooltipBorder;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .apexcharts-tooltip.apexcharts-theme-dark {
        background: ${tooltipBg} !important;
        background-color: ${tooltipBg} !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        border: ${tooltipBorderWidth} solid ${tooltipBorder} !important;
        border-radius: ${tooltipBorderRadius} !important;
        box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2) !important;
        padding: 0 !important;
        color: ${tooltipTextColor} !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark,
      .apexcharts-tooltip.apexcharts-theme-dark * {
        background: ${tooltipBg} !important;
        background-color: ${tooltipBg} !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark > div {
        background: ${tooltipBg} !important;
        background-color: ${tooltipBg} !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark:empty {
        display: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark[style*="opacity: 0"],
      .apexcharts-tooltip.apexcharts-theme-dark[style*="display: none"] {
        display: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {
        background: transparent !important;
        border-bottom: 1px solid ${borderColor} !important;
        color: ${tooltipTextColor} !important;
        font-weight: 600 !important;
        margin: 0 0 8px 0 !important;
        padding: 0 0 8px 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        font-size: 14px !important;
        font-family: 'IBM Plex Sans', sans-serif !important;
        position: relative !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title:empty {
        display: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-series-group {
        color: ${tooltipTextColor} !important;
        display: flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        margin: 0 0 4px 0 !important;
        padding: 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
        font-size: 14px !important;
        font-family: 'IBM Plex Sans', sans-serif !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-series-group:empty {
        display: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-series-group span {
        color: ${tooltipTextColor} !important;
        font-size: 14px !important;
        font-family: 'IBM Plex Sans', sans-serif !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-marker,
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-series-group .apexcharts-tooltip-marker,
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text .apexcharts-tooltip-marker,
      .apexcharts-tooltip.apexcharts-theme-dark span.apexcharts-tooltip-marker,
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-marker svg,
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-marker circle,
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text-y-label:before,
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text-y-value:before {
        display: none !important;
        visibility: hidden !important;
        width: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        opacity: 0 !important;
        content: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text {
        display: flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        color: ${tooltipTextColor} !important;
        font-size: 14px !important;
        font-family: 'IBM Plex Sans', sans-serif !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text-y-label {
        display: inline !important;
        margin: 0 !important;
        margin-right: 8px !important;
        padding: 0 !important;
        color: ${tooltipLabelColor} !important;
        font-size: 14px !important;
        font-family: 'IBM Plex Sans', sans-serif !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-text-y-value {
        display: inline !important;
        margin: 0 !important;
        padding: 0 !important;
        color: ${tooltipTextColor} !important;
        font-size: 14px !important;
        font-family: 'IBM Plex Sans', sans-serif !important;
        font-weight: 600 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [resolvedTheme]);


  // Format currency (with optional sign for P&L values) — uses Profile → display_currency
  const formatCurrency = (value: number, showSign: boolean = false): string => {
    return formatMoney(value, { showSign });
  };
  
  // Format percentage
  const formatPercent = (value: number): string => {
    return `${value.toFixed(2)}%`;
  };

  const layoutItems = layoutItemsForFetch;
  const visibleItems = visibleItemsForFetch;

  const handleLayoutMove = useCallback(
    (sortableId: string, direction: "up" | "down") => {
      const activeLayout = getActiveLayout();
      if (!activeLayout) return;

      preserveViewportScroll(() => {
        const updatedItems = applyDashboardLayoutMove(activeLayout.items, sortableId, direction);
        updateLayoutItems(activeLayout.id, updatedItems);
      });
    },
    [getActiveLayout, updateLayoutItems]
  );

  const getSortableMoveProps = useCallback(
    (sortableId: string) => {
      if (!isEditMode) {
        return {};
      }

      const { canMoveUp, canMoveDown } = getDashboardLayoutMoveState(layoutItems, sortableId);
      return {
        canMoveUp,
        canMoveDown,
        onMoveUp: () => handleLayoutMove(sortableId, "up"),
        onMoveDown: () => handleLayoutMove(sortableId, "down"),
      };
    },
    [handleLayoutMove, isEditMode, layoutItems]
  );

  const miniItemsSorted = useMemo(
    () =>
      layoutItems
        .filter((item) => item.zone === "miniStats")
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [layoutItems]
  );
  const visibleMiniStatCount = useMemo(
    () => miniItemsSorted.filter((i) => i.visible).length,
    [miniItemsSorted]
  );
  const miniDisplayItems = useMemo(() => {
    const visibleOrdered = miniItemsSorted
      .filter((i) => i.visible)
      .slice(0, DASHBOARD_MAX_MINI_STAT_SLOTS);
    if (!isEditMode) {
      return visibleOrdered;
    }
    if (visibleOrdered.length >= DASHBOARD_MAX_MINI_STAT_SLOTS) {
      return visibleOrdered;
    }
    return miniItemsSorted.slice(0, DASHBOARD_MAX_MINI_STAT_SLOTS);
  }, [miniItemsSorted, isEditMode]);
  const miniStatsGridTemplateColumns = useMemo(() => {
    const items = miniDisplayItems
      .filter((item) => item.visible)
      .map((item) => {
        const cardId = item.cardType;
        switch (cardId) {
          case "expectancy":
          case "portfolio": {
            const showExpectancy =
              cardId === "expectancy" ||
              useTradingMetricsStrip ||
              !supportsPortfolioBalanceFromBroker;
            return {
              title: showExpectancy ? "Expectancy" : "Current Balance",
              amount: showExpectancy
                ? formatCurrency(displayExpectancyValue, true)
                : formatCurrency(portfolioSize, false),
            };
          }
          case "winRate":
            return {
              title: "Win Rate",
              amount: formatPercent(winRateStats.winRate || 0),
            };
          case "monthlyPnL":
            return {
              title: "Total P&L",
              amount: formatCurrency(monthlyPnL, true),
            };
          case "roi":
            if (useTradingMetricsStrip) {
              const mdd = tradingMetricsData?.max_drawdown;
              const mddText =
                mdd != null && !Number.isNaN(Number(mdd))
                  ? formatCurrency(Number(mdd), true)
                  : formatCurrency(0, false);
              return { title: "Max Drawdown", amount: mddText };
            }
            return {
              title: "Return on Investment",
              amount: roiPercent !== null ? formatPercent(roiPercent) : "0.00%",
            };
          case "closedTrades":
            return { title: "Closed Trades", amount: `${closedTradesCount}` };
          case "activeTrades":
            return { title: "Active Trades", amount: `${activeTradesCount}` };
          case "largestWin":
            return {
              title: "Largest Win",
              amount: formatCurrency(dashboardStats?.largest_win ?? 0, true),
            };
          case "averageWin":
            return {
              title: "Average Win",
              amount: formatCurrency(dashboardStats?.average_win ?? 0, true),
            };
          case "largestLoss":
            return {
              title: "Largest Loss",
              amount: formatCurrency(dashboardStats?.largest_loss ?? 0, true),
            };
          case "averageLoss":
            return {
              title: "Average Loss",
              amount: formatCurrency(dashboardStats?.average_loss ?? 0, true),
            };
          default:
            return {
              title: dashboardCardRegistry[cardId]?.title ?? cardId,
              amount: "",
            };
        }
      });
    return dashboardMiniStatsGridTemplateColumns(items, {
      isMobile,
      maxColumns: 4,
      gapPx: 24,
    });
  }, [
    miniDisplayItems,
    isMobile,
    useTradingMetricsStrip,
    supportsPortfolioBalanceFromBroker,
    displayExpectancyValue,
    portfolioSize,
    winRateStats.winRate,
    monthlyPnL,
    tradingMetricsData?.max_drawdown,
    roiPercent,
    closedTradesCount,
    activeTradesCount,
    dashboardStats?.largest_win,
    dashboardStats?.average_win,
    dashboardStats?.largest_loss,
    dashboardStats?.average_loss,
    formatMoney,
  ]);
  const visibleMiniCardSlotIds = useMemo(
    () =>
      miniDisplayItems
        .filter((item) => item.visible)
        .map((_, index) => miniStatSlotId(index)),
    [miniDisplayItems]
  );
  const usedVisibleCardTypes = useMemo(
    () => new Set(layoutItems.filter((item) => item.visible).map((item) => item.cardType)),
    [layoutItems]
  );
  const showPortfolioGrowth = visibleCardTypeSet.has("portfolioGrowth");
  const showWinRateTrend = visibleCardTypeSet.has("winRateTrend");
  const showPnlMilestones = visibleCardTypeSet.has("pnlMilestones");
  const showSplitRowSection =
    showPortfolioGrowth || showWinRateTrend || showPnlMilestones || isEditMode;
  const showTradeActivityCalendar = visibleCardTypeSet.has("tradeActivityCalendar");
  const showTradeDistribution = visibleCardTypeSet.has("tradeDistribution");

  const dashboardChartRows = useMemo(
    () => buildDashboardChartRows(layoutItems),
    [layoutItems]
  );

  const chartRowMergePreview = useMemo(() => {
    const live = getChartRowMergePreview(layoutItems, chartRowDragActiveId, chartRowDragOverId);
    if (live) return live;
    if (
      chartRowDragActiveId &&
      chartRowMergePreviewRef.current?.incomingCardType === chartRowDragActiveId
    ) {
      return chartRowMergePreviewRef.current;
    }
    return null;
  }, [layoutItems, chartRowDragActiveId, chartRowDragOverId]);

  const isChartRowDragActive = useMemo(
    () =>
      chartRowDragActiveId != null &&
      isSplittableChartRowCard(chartRowDragActiveId as DashboardCardType),
    [chartRowDragActiveId]
  );

  const chartRowSortableProps = useMemo(
    () => ({
      editMode: isEditMode,
      suppressTransform: isChartRowDragActive,
      disableLayoutAnimation: isChartRowDragActive,
      fillHeight: true,
    }),
    [isChartRowDragActive, isEditMode]
  );

  const chartRowSortableIds = useMemo(
    () => dashboardChartRows.flatMap((row) =>
      row.kind === "full" ? [row.cardType] : [row.left, row.right]
    ),
    [dashboardChartRows]
  );

  const splitRowSortableIds = useMemo(() => {
    const rows = layoutItems.filter(
      (i) =>
        i.zone === "splitRow" &&
        i.visible &&
        SPLIT_ROW_CARD_TYPES.includes(i.cardType)
    );
    return rows
      .sort((a, b) => {
        if (a.slot === "left" && b.slot !== "left") return -1;
        if (b.slot === "left" && a.slot !== "left") return 1;
        return (a.order ?? 0) - (b.order ?? 0);
      })
      .map((i) => i.cardType);
  }, [layoutItems]);

  const splitRowOccupiedSlots = useMemo(() => {
    const slots = new Set<"left" | "right">();
    for (const item of layoutItems) {
      if (
        item.zone !== "splitRow" ||
        !item.visible ||
        !SPLIT_ROW_CARD_TYPES.includes(item.cardType)
      ) {
        continue;
      }
      if (item.slot === "left" || item.slot === "right") {
        slots.add(item.slot);
      }
    }
    // Fall back by order when slot is missing on older layouts.
    if (slots.size === 0 && splitRowSortableIds.length > 0) {
      slots.add("left");
      if (splitRowSortableIds.length > 1) slots.add("right");
    }
    return slots;
  }, [layoutItems, splitRowSortableIds]);

  const openAddWidgetModal = useCallback(
    (
      zone: "miniStats" | "splitRow" | "fullWidth",
      slotCardType: DashboardCardType | null = null,
      anchor: "belowTradeDistribution" | null = null
    ) => {
      setAddWidgetZone(zone);
      setReplaceCardSlot(slotCardType);
      setFullWidthAddAnchor(anchor);
      onAddWidgetModalOpen();
    },
    [onAddWidgetModalOpen]
  );

  const replaceCardInSlot = useCallback(
    (slotCardType: DashboardCardType, selectedCardType: DashboardCardType) => {
      if (!canReplaceDashboardLayoutCard(slotCardType)) return;

      const activeLayout = getActiveLayout();
      if (!activeLayout) return;

      const slotItem = activeLayout.items.find((item) => item.cardType === slotCardType);
      const slotZone = slotItem?.zone ?? "miniStats";
      if (slotZone === "miniStats") {
        const otherVisibleMiniStatTypes = activeLayout.items
          .filter((item) => item.visible && item.zone === "miniStats" && item.cardType !== slotCardType)
          .map((item) => item.cardType);
        if (!canAddDashboardMiniStatCard(selectedCardType, otherVisibleMiniStatTypes)) {
          return;
        }
      }

      const selectedItem = activeLayout.items.find((item) => item.cardType === selectedCardType);
      const fallback = dashboardCardRegistry[selectedCardType].defaultItem;
      const slotOrder = slotItem?.order ?? fallback.order ?? 0;
      const slotRatio = slotItem?.ratio;
      const slotPlacement = slotItem?.slot;
      const isReplacingWithSameCard = slotCardType === selectedCardType;

      const updatedItems = activeLayout.items.map((item) => {
        if (item.cardType === slotCardType) {
          if (isReplacingWithSameCard) {
            return {
              ...item,
              visible: true,
              zone: slotZone,
              order: slotOrder,
              ratio: slotRatio ?? item.ratio,
              slot: slotPlacement ?? item.slot,
            };
          }
          return { ...item, visible: false, order: 100 + slotOrder };
        }
        if (item.cardType === selectedCardType) {
          return {
            ...item,
            visible: true,
            zone: slotZone,
            order: slotOrder,
            ratio: slotRatio ?? item.ratio,
            slot: slotPlacement ?? item.slot,
          };
        }
        return item;
      });

      if (!selectedItem) {
        updatedItems.push({
          ...fallback,
          visible: true,
          zone: slotZone,
          order: slotOrder,
          ratio: slotRatio ?? fallback.ratio,
          slot: slotPlacement ?? fallback.slot,
        });
      }

      updateLayoutItems(activeLayout.id, updatedItems);
    },
    [getActiveLayout, updateLayoutItems]
  );

  const handleMiniStatReplace = useCallback(
    (slotCardType: DashboardCardType, selectedCardType: DashboardCardType) => {
      preserveViewportScroll(() => replaceCardInSlot(slotCardType, selectedCardType))
    },
    [replaceCardInSlot]
  );

  const renderSplitRowCardBody = useCallback(
    (cardType: DashboardCardType): React.ReactNode => {
      if (cardType === "pnlMilestones") {
        return <DashboardMilestonesCard accountCode={effectiveBrokerAccountCode} />;
      }
      if (cardType === "portfolioGrowth") {
        return (
          <DashboardPortfolioGrowthChart
            brokerAccountCode={effectiveBrokerAccountCode}
            timeframe={derivedChartTimeframe}
            chartLocalStart={chartLocalWindow?.localStart}
            chartLocalEnd={chartLocalWindow?.localEnd}
            reportingTimezone={reportingTimeZone}
            portfolioSize={portfolioSize}
            percentage={portfolioGrowthChangePercent ?? 0}
            title={dashboardPortfolioGrowthTitle}
            portfolioGrowthData={portfolioGrowthData}
            portfolioGrowthLoading={dashboardSummaryLoading}
            brokerName={activeBrokerName}
            supportsPortfolioGrowth={supportsPortfolioGrowth}
            defaultGrowthBasis={defaultGrowthBasis}
            useExternalDataOnly={dashboardSummaryLoading || summaryHasPortfolioGrowthSeries}
          />
        );
      }
      if (cardType === "winRateTrend") {
        return (
          <ChartCard
            title="Win Rate Trend"
            percentage={winRateTrendChangePercent}
            chart={
              <WinRateChart
                brokerAccountCode={effectiveBrokerAccountCode}
                period={useHourlyCharts ? "hourly" : "daily"}
                timeframe={derivedChartTimeframe as DashboardSummaryCardTimeframe}
                chartLocalStart={chartLocalWindow?.localStart}
                chartLocalEnd={chartLocalWindow?.localEnd}
                localStartDate={chartLocalWindow?.localStart}
                localEndDate={chartLocalWindow?.localEnd}
                reportingTimezone={reportingTimeZone}
                fullChart={true}
                onLatestCumulativeWinRateChange={setLatestCumulativeWinRate}
                useExternalDataOnly={dashboardSummaryLoading || summaryHasWinRateTrendSeries}
                data={winRateTrendData}
                dataLoading={dashboardSummaryLoading}
                viewOptions={{ averageWinRate: showAverageWinRateDashboard }}
              />
            }
            value={winRateTrendDisplayValue}
            valueFormatter={(val) => `${val.toFixed(2)}%`}
            timeframe={derivedChartTimeframe}
            viewOptions={{
              portfolioValue: true,
              tradingPnlOnly: false,
              transfers: false,
              averageWinRate: showAverageWinRateDashboard,
            }}
            onViewOptionsChange={(opts) => {
              setShowAverageWinRateDashboard(opts.averageWinRate ?? true);
            }}
          />
        );
      }
      return null;
    },
    [
      activeBrokerName,
      chartLocalWindow?.localEnd,
      chartLocalWindow?.localStart,
      dashboardPortfolioGrowthTitle,
      dashboardSummaryLoading,
      defaultGrowthBasis,
      derivedChartTimeframe,
      effectiveBrokerAccountCode,
      portfolioGrowthChangePercent,
      portfolioGrowthData,
      portfolioSize,
      reportingTimeZone,
      showAverageWinRateDashboard,
      supportsPortfolioGrowth,
      summaryHasPortfolioGrowthSeries,
      summaryHasWinRateTrendSeries,
      useHourlyCharts,
      winRateTrendChangePercent,
      winRateTrendData,
      winRateTrendDisplayValue,
    ]
  );

  const renderSplitRowEmptySlot = (slot: "left" | "right", hasSibling: boolean) => {
    const slotCardType: DashboardCardType =
      slot === "left" ? "portfolioGrowth" : "winRateTrend";
    return (
      <Box
        flex={{ base: "none", lg: hasSibling ? 1 : "100%" }}
        minW="0"
        w="100%"
        h="320px"
        border="2px dashed"
        borderColor={useColorModeValue("gray.300", "gray.600")}
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        onClick={() => openAddWidgetModal("splitRow", slotCardType)}
        _hover={{ borderColor: "#38BBD1", bg: useColorModeValue("gray.50", "whiteAlpha.50") }}
      >
        <Text fontSize="xs" color={useColorModeValue("gray.500", "gray.400")}>
          Click to add widget
        </Text>
      </Box>
    );
  };

  const renderEditableCard = useCallback(
    (
      cardType: DashboardCardType,
      zone: DashboardLayoutZone,
      content: React.ReactNode,
      options?: { onReplace?: (nextCardType: DashboardCardType) => void }
    ) => {
      if (!isEditMode) return content;
      if (!isDashboardLayoutCardEditable(cardType, zone)) return content;

      const replaceOptions = canReplaceDashboardLayoutCard(cardType)
        ? getDashboardLayoutReplaceOptions({
            zone,
            slotCardType: cardType,
            usedVisibleCardTypes,
            visibleMiniStatCount,
          })
        : [];

      const onReplace = options?.onReplace ?? ((nextCardType) => replaceCardInSlot(cardType, nextCardType));

      return (
        <DashboardLayoutCardOptionsMenu
          cardType={cardType}
          replaceOptions={replaceOptions}
          canRemove={canRemoveDashboardLayoutCard(cardType, zone)}
          onReplace={onReplace}
          onRemove={() => removeCard(activeLayoutId, cardType)}
        >
          {content}
        </DashboardLayoutCardOptionsMenu>
      );
    },
    [activeLayoutId, isEditMode, removeCard, replaceCardInSlot, usedVisibleCardTypes, visibleMiniStatCount]
  );

  const handleWidgetSelect = useCallback(
    (selectedCardType: DashboardCardType) => {
      const activeLayout = getActiveLayout();
      if (!activeLayout || !addWidgetZone) return;

      if (replaceCardSlot) {
        replaceCardInSlot(replaceCardSlot, selectedCardType);
      } else if (
        addWidgetZone === "fullWidth" &&
        (fullWidthAddAnchor === "belowTradeDistribution" || isSplittableChartRowCard(selectedCardType))
      ) {
        const updatedItems = addSplittableChartRowCard(activeLayout.items, selectedCardType);
        updateLayoutItems(activeLayout.id, updatedItems);
      } else {
        addCard(activeLayout.id, selectedCardType, addWidgetZone);
      }

      setReplaceCardSlot(null);
      setAddWidgetZone(null);
      setFullWidthAddAnchor(null);
    },
    [addCard, addWidgetZone, fullWidthAddAnchor, getActiveLayout, replaceCardInSlot, replaceCardSlot, updateLayoutItems]
  );

  const modalSelectableCards = useMemo(() => {
    if (!addWidgetZone) return [];

    return getDashboardLayoutSelectableCards({
      zone: addWidgetZone,
      replaceCardSlot,
      usedVisibleCardTypes,
      visibleMiniStatCount,
      fullWidthAddAnchor,
    });
  }, [addWidgetZone, fullWidthAddAnchor, replaceCardSlot, usedVisibleCardTypes, visibleMiniStatCount]);

  // Render card based on ID
  const renderCard = (cardId: string) => {
    switch (cardId) {
      case "expectancy":
      case "portfolio": {
        const showExpectancyInsteadOfBalance =
          cardId === "expectancy" || useTradingMetricsStrip || !supportsPortfolioBalanceFromBroker;
        return (
          <MiniStatisticsV2
            title={showExpectancyInsteadOfBalance ? "Expectancy" : "Current Balance"}
            amount={
              showExpectancyInsteadOfBalance
                ? formatCurrency(displayExpectancyValue, true)
                : formatCurrency(portfolioSize, false)
            }
            percentage={showExpectancyInsteadOfBalance ? null : portfolioChangePercent ?? null}
            timeframe={derivedChartTimeframe}
            comparisonText={miniStatsComparisonText}
            chart={
              <PortfolioSizeChart
                portfolioSize={
                  showExpectancyInsteadOfBalance ? displayExpectancyValue : portfolioSize
                }
                portfolioChangePercent={
                  showExpectancyInsteadOfBalance ? null : portfolioChangePercent
                }
                startingBalance={startingBalance}
                useWhiteColor={true}
              />
            }
            dateRange={dashboardPresetChartPeriodLabel(derivedChartTimeframe)}
            tooltip={
              showExpectancyInsteadOfBalance
                ? `Average P&L per closed trade (${tradingMetricsScopeLabel}), same window as Total P&L. Portfolio growth chart may use a different preset when both are shown.`
                : "The total current value of your trading portfolio, including all open positions and available cash. For date-based presets, the server uses your timezone from Display Preferences."
            }
          />
        );
      }
      case "winRate":
        return (
        <MiniStatisticsV2
          title={"Win Rate"}
            amount={formatPercent(winRateStats.winRate || 0)}
            percentage={winRateChangePercent ?? null}
          timeframe={derivedChartTimeframe}
          comparisonText={miniStatsComparisonText}
          backgroundGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
          chart={
            <WinRateChart
                brokerAccountCode={effectiveBrokerAccountCode}
              period={useHourlyCharts ? "hourly" : "daily"}
              timeframe={derivedChartTimeframe as DashboardSummaryCardTimeframe}
              chartLocalStart={chartLocalWindow?.localStart}
              chartLocalEnd={chartLocalWindow?.localEnd}
              localStartDate={chartLocalWindow?.localStart}
              localEndDate={chartLocalWindow?.localEnd}
              reportingTimezone={reportingTimeZone}
              useWhiteColor={true}
              useExternalDataOnly={dashboardSummaryLoading || summaryHasWinRateTrendSeries}
              data={winRateTrendData}
              dataLoading={dashboardSummaryLoading}
            />
          }
            dateRange={dashboardPresetChartPeriodLabel(derivedChartTimeframe)}
            tooltip="The percentage of closed trades that resulted in a profit. For date-based presets, calendar scope follows your saved timezone on the server."
        />
        );
      case "monthlyPnL":
        return (
        <MiniStatisticsV2
            title={"Total P&L"}
          amount={formatCurrency(monthlyPnL, true)}
          percentage={monthlyPnLChangePercent ?? null}
          timeframe={derivedChartTimeframe}
          comparisonText={miniStatsComparisonText}
          chart={
              <TotalPnLChart
                totalPnL={monthlyPnL}
                trades={[]}
              useWhiteColor={true}
            />
          }
            dateRange={dashboardDateRangeShortLabel}
            tooltip="Total Profit and Loss from closed trades for the selected period. Calendar days use your timezone from Display Preferences (server-side). Recent Trades below uses the same exit-date window unless you choose All Time."
        />
        );
      case "roi":
        if (useTradingMetricsStrip) {
          const mdd = tradingMetricsData?.max_drawdown;
          const mddPct = tradingMetricsData?.max_drawdown_percent;
          const mddText =
            mdd != null && !Number.isNaN(Number(mdd))
              ? formatCurrency(Number(mdd), true)
              : formatCurrency(0, false);
          const pctSuffix =
            mddPct != null && !Number.isNaN(Number(mddPct))
              ? ` Peak-to-trough: ${formatPercent(Number(mddPct))}.`
              : "";
          const nClosed = tradingMetricsData?.closed_trades_count;
          const sampleNote =
            typeof nClosed === "number"
              ? ` Closed trades in scope: ${nClosed}.`
              : "";
          return (
            <MiniStatisticsV2
              title="Max Drawdown"
              amount={mddText}
              percentage={null}
              hideComparison
              hideBottomSection
              useNormalStyling
              timeframe={derivedChartTimeframe}
              tooltip={`Largest decline from a peak in cumulative P&L over ${tradingMetricsScopeLabel}. Same window as Total P&L.${pctSuffix}${sampleNote}`}
            />
          );
        }
        return (
          <MiniStatisticsV2
            title={"Return on Investment"}
            amount={roiPercent !== null ? formatPercent(roiPercent) : "0.00%"}
            percentage={null}
            hideComparison={true}
            hideBottomSection={true}
            useNormalStyling={true}
            timeframe="all-time"
            tooltip={`Return on Investment (all-time only; not tied to the page date filter): ${roiPercent !== null ? formatPercent(roiPercent) : "N/A"}. Capital Invested: ${formatCurrency(totalInvested)}`}
          />
        );
      case "closedTrades":
        return (
          <MiniStatisticsV2
            title={"Closed Trades"}
            amount={`${closedTradesCount}`}
            percentage={miniStatPeriodChanges.closedTrades}
            comparisonText={miniStatsComparisonText}
            hideBottomSection
            useNormalStyling
            timeframe={derivedChartTimeframe}
            tooltip="Total number of closed trades in the selected period (same window as the date selector above)."
          />
        );
      case "activeTrades":
        return (
          <MiniStatisticsV2
            title={"Active Trades"}
            amount={`${activeTradesCount}`}
            percentage={miniStatPeriodChanges.activeTrades}
            comparisonText={miniStatsComparisonText}
            hideBottomSection
            useNormalStyling
            timeframe={derivedChartTimeframe}
            tooltip="Open trades count from the statistics API for the selected period. Uses the same date window as other dashboard stats."
          />
        );
      case "largestWin":
        return (
          <MiniStatisticsV2
            title={"Largest Win"}
            amount={formatCurrency(dashboardStats?.largest_win ?? 0, true)}
            percentage={miniStatPeriodChanges.largestWin}
            comparisonText={miniStatsComparisonText}
            hideBottomSection
            useNormalStyling
            timeframe={derivedChartTimeframe}
            tooltip="Largest profitable trade in the selected period. Percentage change compares this window to the prior period of equal length."
          />
        );
      case "averageWin":
        return (
          <MiniStatisticsV2
            title={"Average Win"}
            amount={formatCurrency(dashboardStats?.average_win ?? 0, true)}
            percentage={miniStatPeriodChanges.averageWin}
            comparisonText={miniStatsComparisonText}
            hideBottomSection
            useNormalStyling
            timeframe={derivedChartTimeframe}
            tooltip="Average profit for winning trades in the selected period. Percentage change compares this window to the prior period of equal length."
          />
        );
      case "largestLoss":
        return (
          <MiniStatisticsV2
            title={"Largest Loss"}
            amount={formatCurrency(dashboardStats?.largest_loss ?? 0, true)}
            percentage={miniStatPeriodChanges.largestLoss}
            comparisonText={miniStatsComparisonText}
            hideBottomSection
            useNormalStyling
            timeframe={derivedChartTimeframe}
            tooltip="Largest losing trade in the selected period. Percentage change compares this window to the prior period of equal length."
          />
        );
      case "averageLoss":
        return (
          <MiniStatisticsV2
            title={"Average Loss"}
            amount={formatCurrency(dashboardStats?.average_loss ?? 0, true)}
            percentage={miniStatPeriodChanges.averageLoss}
            comparisonText={miniStatsComparisonText}
            hideBottomSection
            useNormalStyling
            timeframe={derivedChartTimeframe}
            tooltip="Average loss for losing trades in the selected period. Percentage change compares this window to the prior period of equal length."
          />
        );
      default:
        return null;
    }
  };


  // Navbar top: 45px (mobile/tablet), navbar height: ~91px
  // Navbar bottom: 45px + 91px = 136px
  // For a smaller, more balanced gap while maintaining equidistant feel
  const flexProps: any = {
    flexDirection: "column",
    w: "100%",
    minW: 0,
    overflowX: "hidden",
    pt: shadcnInset ? 0 : { base: "120px", md: "100px", lg: "60px", xl: "0px" },
  };
  const headerBg = useColorModeValue("gray.100", "gray.800");
  const addSlotBorderColor = useColorModeValue("gray.300", "gray.600");
  const addSlotHoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const addSlotTitleColor = useColorModeValue("gray.500", "gray.400");
  const addSlotHintColor = useColorModeValue("gray.500", "gray.400");
  const layoutStatusTextColor = useColorModeValue("gray.500", "white");
  const layoutStatusErrorColor = useColorModeValue("red.500", "red.300");
  const activeLayout = getActiveLayout();
  const activeLayoutName = activeLayout?.name ?? "Layout";

  const handleCreateNewLayoutFromMenu = useCallback(() => {
    newLayoutPreviousLayoutIdRef.current = activeLayoutId;
    setRenameModalIntent("createBeforeEdit");
    setRenameDraft("");
    onRenameLayoutModalOpen();
  }, [activeLayoutId, onRenameLayoutModalOpen]);

  const handleEditLayout = useCallback(
    (layoutId: string) => {
      setIsNewLayoutCreateSession(false);
      setActiveLayout(layoutId, { preserveEditorReset: true });
      setEditMode(true, layoutId);
    },
    [setActiveLayout, setEditMode]
  );

  const handleRenameFromActionBar = useCallback(() => {
    const activeLayout = getActiveLayout();
    if (!activeLayout) return;
    setRenameModalIntent("toolbar");
    setRenameDraft(activeLayout.name);
    onRenameLayoutModalOpen();
  }, [getActiveLayout, onRenameLayoutModalOpen]);

  const handleEditToolbarSave = useCallback(() => {
    saveEditModeChanges();
    if (isNewLayoutCreateSession) {
      setIsNewLayoutCreateSession(false);
      newLayoutPreviousLayoutIdRef.current = null;
    }
    toast.success("Layout saved", {
      description: "Your dashboard card arrangement has been updated.",
    });
  }, [isNewLayoutCreateSession, saveEditModeChanges]);

  const handleEditToolbarCancel = useCallback(() => {
    if (isNewLayoutCreateSession) {
      const fallbackId = newLayoutPreviousLayoutIdRef.current;
      abandonNewLayout(activeLayoutId, fallbackId ?? undefined);
      newLayoutPreviousLayoutIdRef.current = null;
      setIsNewLayoutCreateSession(false);
      return;
    }
    cancelEditModeChanges();
  }, [abandonNewLayout, activeLayoutId, cancelEditModeChanges, isNewLayoutCreateSession]);

  const handleSaveRename = useCallback(() => {
    const trimmedName = renameDraft.trim();
    if (!trimmedName) return;

    if (renameModalIntent === "createBeforeEdit") {
      createLayout(trimmedName, { openEditor: true });
      setIsNewLayoutCreateSession(true);
      onRenameLayoutModalClose();
      toast.success("Layout created", {
        description: `"${trimmedName}" is ready to edit.`,
      });
      return;
    }
    const activeLayout = getActiveLayout();
    if (!activeLayout) return;
    renameLayout(activeLayout.id, trimmedName);
    onRenameLayoutModalClose();
    toast.success("Layout renamed", {
      description: `"${trimmedName}" has been saved.`,
    });
  }, [createLayout, getActiveLayout, onRenameLayoutModalClose, renameDraft, renameLayout, renameModalIntent]);

  const handleCancelRename = useCallback(() => {
    if (renameModalIntent === "createBeforeEdit") {
      setRenameDraft("");
      onRenameLayoutModalClose();
      return;
    }
    const activeLayout = getActiveLayout();
    setRenameDraft(activeLayout?.name ?? "");
    onRenameLayoutModalClose();
  }, [getActiveLayout, onRenameLayoutModalClose, renameModalIntent]);

  const handleRenameModalClose = useCallback(() => {
    if (renameModalIntent === "createBeforeEdit") {
      setRenameDraft("");
      onRenameLayoutModalClose();
      return;
    }
    const activeLayout = getActiveLayout();
    setRenameDraft(activeLayout?.name ?? "");
    onRenameLayoutModalClose();
  }, [getActiveLayout, onRenameLayoutModalClose, renameModalIntent]);

  const handleDeleteFromActionBar = useCallback(() => {
    const activeLayout = getActiveLayout();
    if (!activeLayout || !canDeleteLayout(activeLayout.id)) return;
    setDeleteConfirmText("");
    onDeleteLayoutModalOpen();
  }, [canDeleteLayout, getActiveLayout, onDeleteLayoutModalOpen]);

  const handleConfirmDeleteLayout = useCallback(() => {
    const activeLayout = getActiveLayout();
    if (!activeLayout || !canDeleteLayout(activeLayout.id)) {
      onDeleteLayoutModalClose();
      setDeleteConfirmText("");
      return;
    }
    if (deleteConfirmText.trim() !== activeLayout.name.trim()) return;
    const deletedName = activeLayout.name.trim();
    deleteLayout(activeLayout.id);
    setDeleteConfirmText("");
    onDeleteLayoutModalClose();
    toast.success("Layout deleted", {
      description: `"${deletedName}" has been removed.`,
    });
  }, [
    canDeleteLayout,
    deleteConfirmText,
    deleteLayout,
    getActiveLayout,
    onDeleteLayoutModalClose,
  ]);

  const handleDeleteLayoutDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) return;
      setDeleteConfirmText("");
      onDeleteLayoutModalClose();
    },
    [onDeleteLayoutModalClose]
  );

  useEffect(() => {
    if (!isEditMode && !isRenameLayoutModalOpen) {
      onRenameLayoutModalClose();
    }
  }, [isEditMode, isRenameLayoutModalOpen, onRenameLayoutModalClose]);

  const chartRowCardBodyShell = (content: React.ReactNode) => (
    <Box
      w="100%"
      h="100%"
      flex="1"
      display="flex"
      flexDirection="column"
      minH={0}
    >
      {content}
    </Box>
  );

  const renderChartRowCardBody = (cardType: DashboardCardType): React.ReactNode => {
    if (cardType === "pnlOverTime") {
      return chartRowCardBodyShell(
        <PnLOverTimeChart
          filters={statisticsFilters}
          breakdown={useHourlyCharts ? "hourly" : "daily"}
          statistics={dashboardStats}
          statisticsLoading={dashboardStatsLoading}
        />
      );
    }
    if (cardType === "longVsShortPerformance") {
      return chartRowCardBodyShell(
        <ComparativeAnalysis filters={statisticsFilters} statistics={dashboardStats as any} />
      );
    }
    if (cardType === "performanceBreakdown") {
      return chartRowCardBodyShell(
        <PerformanceBreakdown
          symbolStats={symbolStats}
          loading={dashboardStatsLoading || symbolStatsLoading}
          statistics={{
            largestWin: dashboardStats?.largest_win ?? 0,
            largestLoss: dashboardStats?.largest_loss ?? 0,
            closedTrades: dashboardStats?.closed_trades ?? 0,
            winningTrades: dashboardStats?.winning_trades ?? 0,
            losingTrades: dashboardStats?.losing_trades ?? 0,
            totalReturnPercent: dashboardStats?.total_return_percent ?? 0,
            averageReturnPercent: dashboardStats?.average_return_percent ?? 0,
          }}
          backendStats={dashboardStats ? { best_trade: dashboardStats.best_trade, worst_trade: dashboardStats.worst_trade, most_traded_symbol: dashboardStats.most_traded_symbol, most_traded_symbol_count: dashboardStats.most_traded_symbol_count } : null}
        />
      );
    }
    if (cardType === "streakAnalysis") {
      return chartRowCardBodyShell(
        <StreakAnalysis
          statistics={dashboardStats}
          filters={statisticsFilters}
          loading={dashboardStatsLoading}
        />
      );
    }
    if (cardType === "keyRiskMetrics") {
      return chartRowCardBodyShell(
        <RiskMetricsTab
          filters={statisticsFilters}
          dashboardSections="keyMetrics"
          dataOverride={sharedRiskMetricsData}
          loadingOverride={sharedRiskMetricsLoading}
          errorOverride={sharedRiskMetricsError}
        />
      );
    }
    if (cardType === "drawdownOverTime") {
      return chartRowCardBodyShell(
        <RiskMetricsTab
          filters={statisticsFilters}
          dashboardSections="drawdownChart"
          dataOverride={sharedRiskMetricsData}
          loadingOverride={sharedRiskMetricsLoading}
          errorOverride={sharedRiskMetricsError}
        />
      );
    }
    if (cardType === "equityPeakPath") {
      return chartRowCardBodyShell(
        <RiskMetricsTab
          filters={statisticsFilters}
          dashboardSections="equityPeakPath"
          dataOverride={sharedRiskMetricsData}
          loadingOverride={sharedRiskMetricsLoading}
          errorOverride={sharedRiskMetricsError}
        />
      );
    }
    return null;
  };

  const renderDashboardChartRow = (row: DashboardChartRow): React.ReactNode => {
    const rowMt = "24px";
    const mergeDropBorderColor = "#D3D3D3";

    const renderMergeDropSlot = () => (
      <Box
        {...CHART_ROW_COLUMN_PROPS}
        border="2px dashed"
        borderColor={mergeDropBorderColor}
        borderRadius="6px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="transparent"
        pointerEvents="none"
        flexShrink={0}
      >
        <Text
          fontSize="sm"
          color={mergeDropBorderColor}
          fontWeight="medium"
          fontFamily="'IBM Plex Sans', sans-serif"
          textTransform="uppercase"
          opacity={0.85}
        >
          Drop here
        </Text>
      </Box>
    );

    if (row.kind === "split") {
      const splitCards = (
        <Box data-dashboard-chart-row="" {...CHART_ROW_FLEX_PROPS}>
          {[row.left, row.right].map((cardType) => (
            <Box key={cardType} {...CHART_ROW_COLUMN_PROPS}>
              {isEditMode ? (
                <SortableCard id={cardType} {...chartRowSortableProps} {...getSortableMoveProps(cardType)}>
                  {renderEditableCard(cardType, "fullWidth", renderChartRowCardBody(cardType))}
                </SortableCard>
              ) : (
                <Box h="100%" flex="1" display="flex" flexDirection="column" minH={0} w="100%">
                  {renderEditableCard(cardType, "fullWidth", renderChartRowCardBody(cardType))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      );

      return (
        <Box key={`chart-row-${row.order}`} mt={rowMt} w="100%">
          {isEditMode ? (
            <SortableContext items={[row.left, row.right]} strategy={horizontalListSortingStrategy}>
              {splitCards}
            </SortableContext>
          ) : (
            splitCards
          )}
        </Box>
      );
    }

    const isMergeTarget =
      isEditMode && chartRowMergePreview?.anchorCardType === row.cardType;

    if (!isEditMode) {
      return (
        <Box key={`chart-row-${row.order}`} mt={rowMt} w="100%">
          {renderEditableCard(row.cardType, "fullWidth", renderChartRowCardBody(row.cardType))}
        </Box>
      );
    }

    return (
      <Box key={`chart-row-${row.order}`} mt={rowMt} w="100%">
        <SortableCard
          id={row.cardType}
          {...chartRowSortableProps}
          {...getSortableMoveProps(row.cardType)}
          suppressDropPlaceholder={isMergeTarget}
        >
          <Box data-dashboard-chart-row="" {...CHART_ROW_FLEX_PROPS}>
            <Box {...CHART_ROW_COLUMN_PROPS} transition="flex 0.2s ease">
              {renderEditableCard(row.cardType, "fullWidth", renderChartRowCardBody(row.cardType))}
            </Box>
            {isMergeTarget ? renderMergeDropSlot() : null}
          </Box>
        </SortableCard>
      </Box>
    );
  };

  const renderChartRowSection = (): React.ReactNode => {
    if (dashboardChartRows.length === 0) {
      if (!isEditMode) return null;
      return (
        <DashboardChartRowInsertDropZone
          id={chartRowInsertDropId(0)}
          isDragActive={isChartRowDragActive}
        />
      );
    }

    const rowNodes = dashboardChartRows.map((row, rowIndex) => (
      <Fragment
        key={`chart-row-lane-${row.kind === "full" ? row.cardType : `${row.left}-${row.right}`}`}
      >
        {rowIndex === 0 ? (
          <DashboardChartRowInsertDropZone
            id={chartRowInsertDropId(0)}
            isDragActive={isChartRowDragActive}
          />
        ) : null}
        {renderDashboardChartRow(row)}
        <DashboardChartRowInsertDropZone
          id={chartRowInsertDropId(rowIndex + 1)}
          isDragActive={isChartRowDragActive}
        />
      </Fragment>
    ));

    if (isEditMode) {
      return (
        <SortableContext items={chartRowSortableIds} strategy={verticalListSortingStrategy}>
          {rowNodes}
        </SortableContext>
      );
    }

    return <>{rowNodes}</>;
  };

  const renderBottomAddWidgetSlot = (): React.ReactNode => {
    if (!isEditMode) return null;

    return (
      <Box
        mt="24px"
        w="100%"
        minH="200px"
        border="2px dashed"
        borderColor={addSlotBorderColor}
        borderRadius="6px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
        px={4}
        textAlign="center"
        onClick={() => openAddWidgetModal("fullWidth", null)}
        _hover={{ borderColor: "#38BBD1", bg: addSlotHoverBg }}
      >
        <Text fontSize="sm" fontWeight="normal" color={addSlotTitleColor}>
          Click to add widget
        </Text>
        <Text fontSize="xs" color={addSlotHintColor} mt={2}>
          Charts, calendar, trade distribution, and more
        </Text>
      </Box>
    );
  };

  const dashboardHeaderToolbar = useMemo(
    () => (
      <div className="flex shrink-0 flex-nowrap items-center gap-3">
        <DashboardHeaderDateRangePicker
          dateRange={dateRange}
          selectedPresetId={selectedPresetId}
          onDateRangeChange={(dr, opts) => {
            setDateRange(dr);
            if (opts?.presetId) setSelectedPresetId(opts.presetId);
            else setSelectedPresetId(null);
          }}
        />
        <DashboardLayoutsMenu
          iconOnly={isBelowLg}
          onEditLayout={handleEditLayout}
          onCreateNewLayout={handleCreateNewLayoutFromMenu}
        />
        <DashboardHeaderImportButton iconOnly={isBelowLg} />
        <NeedHelpButton
          href="https://help.tesseratrades.com/dashboard"
          iconOnly={isBelowLg}
        />
      </div>
    ),
    [
      dateRange,
      selectedPresetId,
      handleEditLayout,
      handleCreateNewLayoutFromMenu,
      isBelowLg,
    ],
  );

  useEffect(() => {
    if (!shadcnInset) {
      setInsetHeaderActions(null);
    }
    return () => setInsetHeaderActions(null);
  }, [shadcnInset, setInsetHeaderActions]);

  return (
    // @ts-ignore
    <Flex data-dashboard-page="" {...(flexProps as any)}>
      {shadcnInset && insetHeaderMountEl
        ? createPortal(dashboardHeaderToolbar, insetHeaderMountEl)
        : null}
      {/* Greeting and Profile Section */}
      <Flex
        direction={{ base: "column", xl: "row" }}
        gap={2}
        w="100%"
        mb={0}
        align={{ base: "stretch", xl: "center" }}
        wrap="wrap"
      >
        <Box flex={{ base: "1 1 100%", xl: "1 1 0%" }} minW={0} maxW={{ base: "100%", xl: "none" }}>
          <Greeting />
        </Box>
        {!shadcnInset ? (
          <Flex
            flex={{ base: "0 0 auto", xl: "0 0 auto" }}
            w={{ base: "100%", xl: "auto" }}
            pt={{ base: "12px", xl: "0" }}
            pb={{ base: "12px", xl: "0" }}
            px={0}
            alignSelf={{ base: "stretch", xl: "center" }}
            gap={2}
            direction={{ base: "column", sm: "row" }}
          >
            <div data-slot="sidebar-inset-header-actions" className="flex w-full flex-col items-start gap-2 sm:w-auto sm:flex-row sm:items-center">
              {dashboardHeaderToolbar}
            </div>
          </Flex>
        ) : null}
      </Flex>
      {dashboardPageError ? (
        <Box w="100%" mb={4}>
          <StatisticsPageErrorBanner
            title="Couldn't load dashboard"
            message={dashboardPageError.message}
            action={dashboardPageError.action ?? undefined}
            onRetry={() => {
              refetchDashboardSummary();
              window.dispatchEvent(new CustomEvent("tradesUpdated", { detail: { action: "refetch" } }));
            }}
          />
        </Box>
      ) : null}
      {(!isEditMode && isMutating) || persistenceError ? (
        <Box mt="8px">
          {!isEditMode && isMutating && (
            <Text fontSize="xs" color={layoutStatusTextColor}>
              Saving layout changes{pendingMutation ? ` (${pendingMutation})` : ""}...
            </Text>
          )}
          {persistenceError && (
            <Text fontSize="xs" color={layoutStatusErrorColor}>
              Layout sync warning: {persistenceError}
            </Text>
          )}
        </Box>
      ) : null}
      <StatisticsPageErrorProvider suppressWidgetErrors={Boolean(dashboardPageError)}>
      <DndContext
        sensors={sensors}
        collisionDetection={dashboardLayoutCollisionDetection}
        onDragStart={(event: DragStartEvent) => {
          setChartRowDragActiveId(String(event.active.id));
        }}
        onDragOver={(event: DragOverEvent) => {
          const overId = event.over ? String(event.over.id) : null;
          setChartRowDragOverId(overId);
          const preview = getChartRowMergePreview(
            layoutItems,
            String(event.active.id),
            overId
          );
          if (preview) {
            chartRowMergePreviewRef.current = preview;
          } else if (overId !== null) {
            chartRowMergePreviewRef.current = null;
          }
        }}
        onDragEnd={(event: DragEndEvent) => {
          clearLayoutDragState();
          handleDragEnd(event);
        }}
        onDragCancel={clearLayoutDragState}
      >
      {isEditMode && (
        <DashboardEditLayoutToolbar
          layoutName={activeLayoutName}
          showRenameAndDelete={!isNewLayoutCreateSession}
          canDelete={Boolean(activeLayout && canDeleteLayout(activeLayout.id))}
          onRename={handleRenameFromActionBar}
          onDelete={handleDeleteFromActionBar}
          onCancel={handleEditToolbarCancel}
          onSave={handleEditToolbarSave}
        />
      )}
        <SortableContext items={visibleMiniCardSlotIds} strategy={rectSortingStrategy}>
          <Box
            as="div"
            w="100%"
            minW={0}
            display="grid"
            gridTemplateColumns={miniStatsGridTemplateColumns}
            gap="24px"
            mt="24px"
            alignItems="start"
            gridAutoRows="min-content"
            data-mini-stats-grid
          >
            {miniDisplayItems.map((item, displayIndex) => {
              const visibleSlotIndex = miniDisplayItems
                .slice(0, displayIndex)
                .filter((entry) => entry.visible).length;
              const slotId = miniStatSlotId(visibleSlotIndex);

              return (
              <Box
                key={item.visible ? slotId : `mini-stat-placeholder-${item.cardType}`}
                position="relative"
                w="100%"
                alignSelf="start"
              >
                {item.visible ? (
                <SortableCard id={slotId} editMode={isEditMode} {...getSortableMoveProps(slotId)}>
                  {renderEditableCard(item.cardType, "miniStats", renderCard(item.cardType), {
                    onReplace: (nextCardType) => handleMiniStatReplace(item.cardType, nextCardType),
                  })}
                </SortableCard>
                ) : isEditMode && visibleMiniStatCount < DASHBOARD_MAX_MINI_STAT_SLOTS ? (
                  <Box
                    h={{ base: "108px", sm: "128px", md: "140px", lg: "155px", xl: "155px" }}
                    border="2px dashed"
                    borderColor={useColorModeValue("gray.300", "gray.600")}
                    borderRadius="6px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    onClick={() => openAddWidgetModal("miniStats", item.cardType)}
                    _hover={{ borderColor: "#38BBD1", bg: useColorModeValue("gray.50", "whiteAlpha.50") }}
                  >
                    <Text fontSize="xs" color={useColorModeValue("gray.500", "gray.400")}>
                      Click to add widget
                    </Text>
                  </Box>
                ) : null}
              </Box>
            )})}
          </Box>
        </SortableContext>
      {(showSplitRowSection) && (
        <Box
          id="split-row-container"
          mt="24px"
          mb="26px"
          position="relative"
          display="flex"
          flexDirection={{ base: "column", lg: "row" }}
          gap={{ base: "24px", lg: "24px" }}
          w="100%"
        >
          {splitRowSortableIds.length === 2 ? (
            isEditMode ? (
              <SortableContext items={splitRowSortableIds} strategy={horizontalListSortingStrategy}>
                {splitRowSortableIds.map((ct) => (
                  <SortableCard key={ct} id={ct} editMode={isEditMode} {...getSortableMoveProps(ct)}>
                    <Box flex={{ base: "none", lg: 1 }} minW="0" w="100%">
                      {renderEditableCard(ct, "splitRow", renderSplitRowCardBody(ct))}
                    </Box>
                  </SortableCard>
                ))}
              </SortableContext>
            ) : (
              <>
                {splitRowSortableIds.map((ct) => (
                  <Box key={ct} flex={{ base: "none", lg: 1 }} minW="0" w="100%" position="relative">
                    {renderSplitRowCardBody(ct)}
                  </Box>
                ))}
              </>
            )
          ) : (
            <>
              {(["left", "right"] as const).map((slot) => {
                const cardType = layoutItems.find(
                  (item) =>
                    item.zone === "splitRow" &&
                    item.visible &&
                    SPLIT_ROW_CARD_TYPES.includes(item.cardType) &&
                    (item.slot === slot ||
                      (!item.slot &&
                        splitRowSortableIds[slot === "left" ? 0 : 1] === item.cardType))
                )?.cardType;
                if (cardType) {
                  return (
                    <Box key={cardType} flex={{ base: "none", lg: 1 }} minW="0" w="100%">
                      {renderEditableCard(cardType, "splitRow", renderSplitRowCardBody(cardType))}
                    </Box>
                  );
                }
                if (isEditMode) {
                  return (
                    <Fragment key={`empty-${slot}`}>
                      {renderSplitRowEmptySlot(slot, splitRowSortableIds.length > 0)}
                    </Fragment>
                  );
                }
                return null;
              })}
            </>
          )}
        </Box>
      )}
      {showTradeActivityCalendar &&
        renderEditableCard(
          "tradeActivityCalendar",
          "fullWidth",
          <Box mt={showSplitRowSection ? 0 : "24px"} w="100%">
            <TradeActivityCalendar
              dailyActivityData={dailyActivityData}
              brokerAccountCode={effectiveBrokerAccountCode}
              reportingTimeZone={dailyActivityTimeZone}
            />
          </Box>
        )}
      {showTradeDistribution &&
        renderEditableCard(
          "tradeDistribution",
          "fullWidth",
          <Box mt="24px" w="100%">
            <TradeDistributionCharts
              filters={statisticsFilters}
              panel="tradeDistribution"
              includeSession={false}
            />
          </Box>
        )}
      {renderChartRowSection()}
      {renderBottomAddWidgetSlot()}

      </DndContext>
      </StatisticsPageErrorProvider>

      <DashboardAddWidgetModal
        open={isAddWidgetModalOpen}
        onOpenChange={(open) => {
          if (!open) onAddWidgetModalClose();
        }}
        onCancel={() => {
          setAddWidgetZone(null);
          setReplaceCardSlot(null);
          setFullWidthAddAnchor(null);
          onAddWidgetModalClose();
        }}
        zone={addWidgetZone}
        selectableCards={modalSelectableCards}
        onSelectCard={handleWidgetSelect}
      />

      <DashboardRenameLayoutDialog
        open={isRenameLayoutModalOpen}
        intent={renameModalIntent}
        value={renameDraft}
        onValueChange={setRenameDraft}
        onOpenChange={(open) => {
          if (!open) handleRenameModalClose();
        }}
        onCancel={handleCancelRename}
        onSave={handleSaveRename}
      />

      <DashboardDeleteLayoutDialog
        open={isDeleteLayoutModalOpen}
        layoutName={activeLayoutName}
        confirmText={deleteConfirmText}
        onConfirmTextChange={setDeleteConfirmText}
        onOpenChange={handleDeleteLayoutDialogOpenChange}
        onCancel={() => handleDeleteLayoutDialogOpenChange(false)}
        onConfirmDelete={handleConfirmDeleteLayout}
      />

    </Flex>
  );
}

export default function Dashboard() {
  const shadcnInset = useShadcnInsetChrome();
  const pageMinHeight = statisticsPageMinHeight(shadcnInset);
  const {
    activeAccountCode,
    activeBrokerName,
    accounts: tradeAccounts,
    loading: accountsLoading,
  } = useActiveBrokerAccount();

  const defaultLayoutVariant = useMemo((): DashboardDefaultLayoutVariant => {
    const activeAccount = tradeAccounts.find(
      (account) =>
        account.accountCode === activeAccountCode && account.brokerName === activeBrokerName
    );
    if (activeAccount && isTradeAccountCsvOnlyForUi(activeAccount)) {
      return "csv";
    }
    return "sync";
  }, [activeAccountCode, activeBrokerName, tradeAccounts]);

  const layoutAccountScope = useMemo((): DashboardLayoutAccountScope | null => {
    const candidate: DashboardLayoutAccountScope = {
      brokerName: activeBrokerName ?? "",
      brokerAccountCode: activeAccountCode ?? "",
    };
    if (!isDashboardLayoutAccountScopeReady(candidate)) {
      return null;
    }
    return {
      brokerName: candidate.brokerName.trim(),
      brokerAccountCode: candidate.brokerAccountCode.trim(),
    };
  }, [activeAccountCode, activeBrokerName]);

  const layoutPersistenceReady =
    !accountsLoading &&
    (layoutAccountScope != null || tradeAccounts.length === 0);

  const resolvedLayoutScope = useMemo((): DashboardLayoutAccountScope => {
    if (layoutAccountScope) return layoutAccountScope;
    return {
      brokerName: "__no_account__",
      brokerAccountCode: "none",
    };
  }, [layoutAccountScope]);

  const persistenceAdapter = useMemo(() => {
    return createBackendDashboardLayoutAdapter(resolvedLayoutScope);
  }, [resolvedLayoutScope]);

  const providerKey = dashboardLayoutAccountScopeKey(resolvedLayoutScope);

  if (!layoutPersistenceReady) {
    return (
      <Flex
        data-dashboard-page=""
        direction="column"
        w="100%"
        minH={pageMinHeight}
        align="center"
        justify="center"
      >
        <StatisticsEmptyState
          variant="loading"
          fillParent
          title="Loading dashboard"
          description="Please wait while we load your dashboard."
        />
      </Flex>
    );
  }

  return (
    <DashboardLayoutProvider
      key={providerKey}
      persistenceAdapter={persistenceAdapter}
      defaultLayoutVariant={defaultLayoutVariant}
    >
      <DashboardContent />
    </DashboardLayoutProvider>
  );
}

